'use client';

import { Color } from '@diritto/common/style/color';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { HeaderNavBarHeight, isPC } from 'constants/layout';
import { ZIndex } from 'constants/zIndex';
import { DirittoAppBridge } from 'libs/DirittoAppBridge';
import { useRouter } from 'next/navigation';
import React, { ReactNode, useCallback } from 'react';
import { Divider } from 'ui-components/common/Divider';
import { Footer } from 'ui-components/common/Footer';
import { SafeAreaBottomStyles, ViewPortHeight } from 'ui-components/layout/styles';
import { GlobalNavigationBar } from 'ui-components/navigations/GlobalNavigationBar';
import { BackButton } from 'ui-components/navigations/HeaderButtons';
import { HeaderNavigationBar } from 'ui-components/navigations/HeaderNavigationBar';
import { useClient } from 'ui-hooks/useClient';
import { useViewPort } from 'ui-hooks/useViewPort';

interface CpLoginLayoutProps {
  children?: ReactNode;
  title: string;
  onClickBack?: () => void;
  leftButtons?: ReactNode[];
}

export const PartnerLoginLayout = ({ children, title, onClickBack, leftButtons }: CpLoginLayoutProps) => {
  const client = useClient();
  const showWebHeader = !client.isApp;
  const { height: viewportHeight } = useViewPort();
  const router = useRouter();

  const handleBack = useCallback(
    () => (client.isWeb ? router.back() : DirittoAppBridge.closeSignView()),
    [client.isWeb, router],
  );

  const defaultLeftButtons = [<BackButton key="back" onClick={onClickBack || handleBack} />];

  return (
    <StyledWrapper height={viewportHeight}>
      <StyledGnbContainer>
        <GlobalNavigationBar showPartnersLogo />
        <Divider />
      </StyledGnbContainer>
      <StyledContainer>
        {showWebHeader && (
          <LoginHeaderWrapper>
            <HeaderNavigationBar title={title} leftButtons={leftButtons || defaultLeftButtons} />
          </LoginHeaderWrapper>
        )}
        {children}
      </StyledContainer>
      <FooterStyledWrapper>
        <Footer />
      </FooterStyledWrapper>
    </StyledWrapper>
  );
};

const FooterStyledWrapper = styled.div`
  display: none;

  @media (${isPC}) {
    display: block;
    padding-bottom: 32px;
    padding-top: 8px;
  }
`;

const StyledWrapper = styled.div(
  ({ height }: { height: number }) => css`
    display: flex;
    flex-direction: column;
    width: 100%;
    ${ViewPortHeight(height)};
  `,
);

const StyledGnbContainer = styled.nav`
  position: relative;
  background-color: ${Color.White};
  width: 100%;
  display: none;
  @media (${isPC}) {
    display: block;
  }
`;

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;

  ${SafeAreaBottomStyles()};

  @media (${isPC}) {
    margin: auto auto 120px;
    justify-content: center;
    align-items: center;
  }
`;

const LoginHeaderWrapper = styled.nav`
  position: relative;
  background-color: ${Color.White};
  width: 100%;
  height: ${HeaderNavBarHeight}px;
  z-index: ${ZIndex.NavBar};
  border-bottom: 1px solid ${Color.Gray300};

  @media (${isPC}) {
    display: none;
  }
`;
